import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { getAllUsers, getAllBookings, getAssets, createAsset, logout } from '../utils/api';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

// Deployment Controls Component
const DeploymentControls = ({ asset, onUpdate }) => {
  const [deploymentDays, setDeploymentDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const handleStartDeployment = async () => {
    if (!window.confirm(`Start ${deploymentDays}-day deployment countdown for ${asset.name}?`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/asset/${asset.id}/start-deployment?deployment_days=${deploymentDays}`,
        {},
        { withCredentials: true }
      );
      toast.success('Deployment timer started');
      onUpdate();
    } catch (error) {
      toast.error('Failed to start deployment');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDeployed = async () => {
    if (!window.confirm(`Mark ${asset.name} as deployed?`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/asset/${asset.id}/mark-deployed`,
        {},
        { withCredentials: true }
      );
      toast.success('Asset marked as deployed');
      onUpdate();
    } catch (error) {
      toast.error('Failed to mark as deployed');
    } finally {
      setLoading(false);
    }
  };

  const deploymentStatus = asset.deployment_status || 'waiting_for_tickets';

  return (
    <div className="flex flex-col gap-2">
      {deploymentStatus === 'waiting_for_tickets' && (
        <p className="text-xs text-gray-500">Waiting for all tickets to be sold</p>
      )}
      
      {deploymentStatus === 'under_registration' && (
        <div className="flex flex-col gap-2">
          <Input
            type="number"
            value={deploymentDays}
            onChange={(e) => setDeploymentDays(parseInt(e.target.value))}
            className="w-24"
            min="1"
            max="30"
            disabled={loading}
          />
          <Button
            onClick={handleStartDeployment}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-3 py-1"
            size="sm"
          >
            {loading ? 'Starting...' : 'Start Timer'}
          </Button>
        </div>
      )}
      
      {deploymentStatus === 'deployment_countdown' && (
        <Button
          onClick={handleMarkDeployed}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1"
          size="sm"
        >
          {loading ? 'Deploying...' : 'Mark Deployed'}
        </Button>
      )}
      
      {deploymentStatus === 'deployed' && (
        <span className="text-xs text-green-600 font-semibold">✓ Deployed</span>
      )}
    </div>
  );
};

const AdminDashboard = ({ user, setUser }) => {
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [assetForm, setAssetForm] = useState({
    category: 'vehicle',
    name: '',
    description: '',
    images: '',
    price_per_ticket: '',
    total_tickets: '',
    features: '',
    status: 'active'
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, bookingsRes, assetsRes] = await Promise.all([
        getAllUsers(),
        getAllBookings(),
        getAssets()
      ]);
      setUsers(usersRes.data);
      setBookings(bookingsRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      const images = assetForm.images.split(',').map(url => url.trim());
      const features = assetForm.features.split(',').map(f => f.trim()).filter(f => f);
      
      await createAsset({
        ...assetForm,
        images,
        features,
        price_per_ticket: parseFloat(assetForm.price_per_ticket),
        total_tickets: parseInt(assetForm.total_tickets)
      });
      
      toast.success('Asset created successfully!');
      setShowAssetForm(false);
      setAssetForm({
        category: 'vehicle',
        name: '',
        description: '',
        images: '',
        price_per_ticket: '',
        total_tickets: '',
        features: '',
        status: 'active'
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create asset');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      toast.success('Logged out');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <nav className="bg-white shadow-sm py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/">
            <img src="https://customer-assets.emergentagent.com/job_haute-assets/artifacts/pvnymrfc_THC%20Final%20Logo%20%28JPG%29.1.png" alt="The Haute Circle" className="h-16" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-gray">Admin: {user.name}</span>
            <Link to="/member-dashboard"><Button variant="outline">My Dashboard</Button></Link>
            <Button onClick={handleLogout} variant="outline" data-testid="admin-logout-btn">Logout</Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-20">
        <h1 className="text-4xl font-bold mb-12" data-testid="admin-heading">Admin Dashboard</h1>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray mb-2">Total Users</p>
            <p className="text-3xl font-bold" data-testid="total-users">{users.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray mb-2">Total Bookings</p>
            <p className="text-3xl font-bold">{bookings.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray mb-2">Total Assets</p>
            <p className="text-3xl font-bold">{assets.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray mb-2">Total Revenue</p>
            <p className="text-3xl font-bold">₹{bookings.reduce((sum, b) => sum + b.total_paid, 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Asset Management */}
        <section className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Asset Management</h2>
            <Button onClick={() => setShowAssetForm(!showAssetForm)} className="bg-gold hover:bg-gold/90 text-black" data-testid="create-asset-btn">
              {showAssetForm ? 'Cancel' : 'Create New Asset'}
            </Button>
          </div>

          {showAssetForm && (
            <div className="bg-white p-8 rounded-lg shadow-lg mb-6">
              <form onSubmit={handleCreateAsset} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Category</label>
                    <select
                      value={assetForm.category}
                      onChange={(e) => setAssetForm({...assetForm, category: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg"
                      required
                      data-testid="asset-category"
                    >
                      <option value="vehicle">Vehicle</option>
                      <option value="jet">Jet</option>
                      <option value="home">Home</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <Input
                      value={assetForm.name}
                      onChange={(e) => setAssetForm({...assetForm, name: e.target.value})}
                      required
                      data-testid="asset-name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Textarea
                    value={assetForm.description}
                    onChange={(e) => setAssetForm({...assetForm, description: e.target.value})}
                    required
                    rows={3}
                    data-testid="asset-description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Image URLs (comma-separated)</label>
                  <Textarea
                    value={assetForm.images}
                    onChange={(e) => setAssetForm({...assetForm, images: e.target.value})}
                    required
                    rows={2}
                    placeholder="https://image1.jpg, https://image2.jpg"
                    data-testid="asset-images"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Price per Ticket (₹)</label>
                    <Input
                      type="number"
                      value={assetForm.price_per_ticket}
                      onChange={(e) => setAssetForm({...assetForm, price_per_ticket: e.target.value})}
                      required
                      data-testid="asset-price"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Total Tickets</label>
                    <Input
                      type="number"
                      value={assetForm.total_tickets}
                      onChange={(e) => setAssetForm({...assetForm, total_tickets: e.target.value})}
                      required
                      data-testid="asset-tickets"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Features (comma-separated)</label>
                  <Textarea
                    value={assetForm.features}
                    onChange={(e) => setAssetForm({...assetForm, features: e.target.value})}
                    rows={2}
                    placeholder="Feature 1, Feature 2, Feature 3"
                  />
                </div>

                <Button type="submit" className="w-full bg-black text-white hover:bg-black/90" data-testid="submit-asset-btn">
                  Create Asset
                </Button>
              </form>
            </div>
          )}

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-bold mb-4">Existing Assets</h3>
            <div className="space-y-4">
              {assets.map((asset) => (
                <div key={asset.id} className="border rounded-lg p-4" data-testid={`admin-asset-${asset.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-lg">{asset.name}</p>
                      <p className="text-sm text-gray-600">{asset.category} - ₹{asset.price_per_ticket.toLocaleString()}/ticket</p>
                      <p className="text-sm text-gray-600">Sold: {asset.sold_tickets}/{asset.total_tickets} tickets</p>
                      <p className="text-sm font-medium mt-1">
                        Status: <span className={`${
                          asset.deployment_status === 'deployed' ? 'text-green-600' :
                          asset.deployment_status === 'deployment_countdown' ? 'text-purple-600' :
                          'text-yellow-600'
                        }`}>
                          {asset.deployment_status || 'waiting_for_tickets'}
                        </span>
                      </p>
                    </div>
                    <DeploymentControls asset={asset} onUpdate={loadData} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Users */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Users</h2>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3">Name</th>
                    <th className="text-left py-3">Email</th>
                    <th className="text-left py-3">Role</th>
                    <th className="text-left py-3">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b" data-testid={`admin-user-${u.id}`}>
                      <td className="py-3">{u.name}</td>
                      <td className="py-3">{u.email}</td>
                      <td className="py-3">{u.role}</td>
                      <td className="py-3">{new Date(u.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Bookings */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Bookings</h2>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3">User ID</th>
                    <th className="text-left py-3">Asset ID</th>
                    <th className="text-left py-3">Tickets</th>
                    <th className="text-left py-3">Amount</th>
                    <th className="text-left py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b" data-testid={`admin-booking-${b.id}`}>
                      <td className="py-3">{b.user_id.substring(0, 8)}...</td>
                      <td className="py-3">{b.asset_id.substring(0, 8)}...</td>
                      <td className="py-3">{b.tickets_purchased}</td>
                      <td className="py-3">₹{b.total_paid.toLocaleString()}</td>
                      <td className="py-3">{b.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
